import { Component, AfterViewInit, Input, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { IPictureData } from '../../shared/components/picture/picture.component';
import { IUProperty } from '../../shared/interface/umbraco-property';
import { applyTitleSettings } from '../../shared/utils/title-style-settings-utils';
import { ITitleStyleSettings } from '../../shared/interface/title-style-settings';

interface IReferencesData {
  title?: IUProperty<string>;
  items?: IUProperty<IReferencesItemData[]>;

  titleStyleSettingsComposition?: ITitleStyleSettings;
}

interface IReferencesItemData {
  image: IUProperty<IPictureData>;
}

@Component({
  selector: 'app-references-panel',
  templateUrl: './references-panel.component.html',
  styleUrls: ['./references-panel.component.less']
})
export class ReferencesPanelComponent implements AfterViewInit {
  @Input() data: IReferencesData;

  private lastXPosition: number;
  private isMouseDown: boolean = false;
  private slidePosition: number;
  private isSlidingEnabled: boolean = false;
  private countDown: Subscription;
  private animationDurationMs: number = 100;
  private animationStep: number = -5;

  private static readonly cyclePause = 200;
  private timeLeft: number = ReferencesPanelComponent.cyclePause;

  @ViewChild('panelElRef', { static: false }) panelEl: ElementRef<HTMLElement>;
  @ViewChild('itemsElRef', { static: false }) itemsEl: ElementRef<HTMLElement>;
  @ViewChild("titleEl", { static: false }) titleEl: ElementRef<HTMLElement>;

  @HostListener('window:mouseup')
  @HostListener('window:touchend')
  @HostListener('window:touchcancel')
  onMouseup() {
    this.isMouseDown = false;
  }

  @HostListener('window:mousemove', ['$event'])
  onMousemove(event: MouseEvent) {
    if (!this.isMouseDown) return;

    let distance = event.clientX - this.lastXPosition;
    this.setSlidePosition(distance);
    this.lastXPosition = event.clientX;
  }

  @HostListener('window:touchmove', ['$event'])
  onTouchmove(event: TouchEvent) {
    if (!this.isMouseDown) return;

    let distance = event.touches[0].clientX - this.lastXPosition;
    this.setSlidePosition(distance);
    this.lastXPosition = event.touches[0].clientX;
  }

  @HostListener('mousedown', ['$event'])
  onMousedown(event: MouseEvent) {
    if (event.button !== 0) return;

    this.isMouseDown = true;
    this.lastXPosition = event.clientX;
    this.stopTimer();
  }

  @HostListener('touchstart', ['$event'])
  onTouchstart(event: TouchEvent) {
    this.isMouseDown = true;
    this.lastXPosition = event.touches[0].clientX;
    this.stopTimer();
  }

  ngAfterViewInit(): void {
    applyTitleSettings(this.data, this.titleEl)

    this.slidePosition = this.itemsEl.nativeElement.offsetLeft;

    setTimeout(() => {
      if (this.itemsEl.nativeElement.scrollWidth > this.itemsEl.nativeElement.clientWidth) {
        this.isSlidingEnabled = true;
        this.startTimer();
      }
    }, 1000);
  }

  setSlidePosition(distance: number): boolean {
    if (!this.isSlidingEnabled) return;

    let overlap = -(this.itemsEl.nativeElement.scrollWidth - this.itemsEl.nativeElement.clientWidth);
    this.slidePosition += distance;

    let canContinueSliding = true;
    if (this.slidePosition < overlap) {
      this.slidePosition = overlap;
      canContinueSliding = false;
    } else if (this.slidePosition > 0) {
      this.slidePosition = 0;
      canContinueSliding = false;
    }

    this.slideItems();
    return canContinueSliding;
  }

  slideItems(): void {
    this.itemsEl.nativeElement.style.transform = `translate3d(${this.slidePosition}px, 0px, 0px)`;
    this.itemsEl.nativeElement.style.transition = `all ${this.animationDurationMs}ms ease-in-out`;
  }

  startTimer() {
    if (this.data.items.value.length === 1) return;

    const source = timer(0, this.animationDurationMs / 2);
    this.countDown = source.subscribe(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        return;
      }

      var canContinueSliding = this.setSlidePosition(this.animationStep);
      if (!canContinueSliding) {
        this.animationStep = -this.animationStep;
        this.timeLeft = ReferencesPanelComponent.cyclePause;
      }
    });
  }

  stopTimer() {
    this.countDown.unsubscribe();
    this.animationDurationMs = 50;
  }
}
